import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Modal, message } from 'antd';
import FileUploader from '@components/FileUploader';
import Papa from 'papaparse';
import { RcFile } from 'antd/lib/upload/interface';
import { useTranslation } from 'react-i18next';
import CsvExport from '@components/CsvExport';
import { DownloadOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';

interface SelectByCsVModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onUpload: (customers: { [key: string]: string }[]) => void;
}

const SelectByCsvModal = ({
  isOpen,
  onCancel,
  onUpload,
}: SelectByCsVModalProps) => {
  const { t } = useTranslation(['device-actions-modal', 'attributes']);
  const [uploadedFile, setUploadedFile] = useState();
  const { theme } = useContext(SessionContext);
  const [fileUploaded, setFileUploaded] = useState<RcFile>();
  const [uploadedCustomers, setUploadedCustomers] = useState<
    {
      [key: string]: string;
    }[]
  >();

  const fileModelData = useMemo(
    () =>
      ['14621668730', '93791907000105', '87616223540']?.map(cpfCnpj => ({
        cpfCnpj,
      })),
    [],
  );

  const fileModelColumns = useMemo(
    () => [
      {
        key: 'cpfCnpj',
        title: t('attributes:cpfCnpj'),
        dataIndex: 'cpfCnpj',
      },
    ],
    [t],
  );

  useEffect(() => {
    if (fileUploaded) {
      Papa.parse(fileUploaded, {
        skipEmptyLines: 'greedy',
        header: true,
        complete(results: Papa.ParseResult<{ [key: string]: string }>) {
          const csvHeaderReference = fileModelColumns.map(item => item.title);
          if (
            JSON.stringify(csvHeaderReference) !==
            JSON.stringify(results.meta.fields)
          ) {
            message.error(t('invalid-header'));
            setUploadedCustomers([]);
            return;
          }

          try {
            const customersToSend: { [key: string]: string }[] =
              results.data.map((device: { [key: string]: string }) => {
                const rowObject: { [key: string]: string } = {};

                fileModelColumns.forEach(header => {
                  rowObject[header.dataIndex] = device[header.title];
                });
                return rowObject;
              });

            setUploadedCustomers(customersToSend);
          } catch (err: any) {
            message.error(
              t('invalid-data', {
                register: err?.message,
              }),
            );
            setUploadedCustomers([]);
          }
        },
      });
    } else {
      setUploadedCustomers([]);
    }
  }, [fileModelColumns, fileUploaded, t]);

  const handleClose = useCallback(() => {
    setUploadedCustomers([]);
    setFileUploaded(undefined);
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    onUpload(uploadedCustomers ?? []);
    handleClose();
  }, [handleClose, onUpload, uploadedCustomers]);

  return (
    <Modal
      title={t('select-customers.select-recipients')}
      open={isOpen}
      onCancel={handleClose}
      bodyStyle={{ padding: '16px 40px' }}
      footer={
        <Button
          type="primary"
          onClick={handleConfirm}
          disabled={uploadedCustomers?.length === 0}
        >
          {t('select-customers.confirm-button')}
        </Button>
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <span style={{ color: theme?.colors?.text1 }}>
          {t('select-customers.select-by-document-number')}
        </span>
        <CsvExport
          data={fileModelData}
          filename="model-import.csv"
          columns={fileModelColumns}
          separator=","
        >
          <Button icon={<DownloadOutlined />} style={{ margin: 0 }}>
            {t('download-model')}
          </Button>
        </CsvExport>
      </div>
      <FileUploader
        uploadColumns={[
          { title: t('attributes:cpfCnpj'), dataIndex: 'cpfCnpj' },
        ]}
        onUpload={setUploadedCustomers}
        style={{ marginTop: 10 }}
        filesListState={[uploadedFile, setUploadedFile]}
      />
    </Modal>
  );
};

export default SelectByCsvModal;
